import React, { useState, useEffect } from 'react';
import { SwedishTask, DailyRecord } from '../types';
import { DEFAULT_SWEDISH_TASKS } from '../lib/storage';
import { BookOpen, CheckCircle, GraduationCap, Clock, Sparkles, RefreshCw, Key, Loader2 } from 'lucide-react';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { apiGet, apiPost } from '../lib/api';

interface SwedishProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
}

export default function SwedishModule({ record, onUpdateRecord }: SwedishProps) {
  const { lang, t } = useLanguage();
  const [completedTaskIds, setCompletedTaskIds] = useState<string[]>([]);
  const [tips, setTips] = useState<string[]>([]);
  const [keyword, setKeyword] = useState<string>('Arbetskamrat');
  const [keywordPronounce, setKeywordPronounce] = useState<string>('آربِتز-کام-رات - همکار');
  const [isLoadingTips, setIsLoadingTips] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  // Load progress from D1 on mount or record date change
  useEffect(() => {
    let isMounted = true;
    const fetchProgress = async () => {
      try {
        const currentDate = record?.date || new Date().toISOString().split('T')[0];
        const res = await apiGet<{ completed_task_ids: string[] }>(`/api/swedish/progress?date=${currentDate}`);
        if (isMounted && res.success && res.data) {
          setCompletedTaskIds(res.data.completed_task_ids || []);
        }
      } catch (err) {
        console.warn('Error fetching Swedish progress from D1:', err);
      }
    };

    fetchProgress();
    return () => { isMounted = false; };
  }, [record?.date]);

  // Load AI tips from D1 history on mount or language change
  useEffect(() => {
    let isMounted = true;
    const fetchTipsHistory = async () => {
      try {
        const res = await apiGet<Array<{ tips: string[]; keyword: string; keyword_pronounce: string }>>('/api/swedish/tips/history');
        if (isMounted && res.success && Array.isArray(res.data) && res.data.length > 0) {
          const latest = res.data[0];
          setTips(latest.tips || []);
          if (latest.keyword) setKeyword(latest.keyword);
          if (latest.keyword_pronounce) setKeywordPronounce(latest.keyword_pronounce);
        } else if (isMounted) {
          // If no history exists, generate initial tips via Cloudflare AI Worker
          generateNewAiTips();
        }
      } catch (err) {
        console.warn('Error fetching Swedish tips history:', err);
      }
    };

    fetchTipsHistory();
    return () => { isMounted = false; };
  }, [lang]);

  // Call real Cloudflare Worker AI to generate 3 new tips
  const generateNewAiTips = async () => {
    setIsLoadingTips(true);
    setIsRotating(true);
    try {
      const res = await apiPost<{ tips: string[]; keyword: string; keyword_pronounce: string }>('/api/swedish/tips/generate', { lang });
      if (res.success && res.data) {
        setTips(res.data.tips || []);
        if (res.data.keyword) setKeyword(res.data.keyword);
        if (res.data.keyword_pronounce) setKeywordPronounce(res.data.keyword_pronounce);
      }
    } catch (err) {
      console.error('Failed to generate AI tips:', err);
    } finally {
      setIsLoadingTips(false);
      setTimeout(() => setIsRotating(false), 500);
    }
  };

  const toggleTaskCompletion = async (taskId: string) => {
    let updatedIds = [...completedTaskIds];
    if (updatedIds.includes(taskId)) {
      updatedIds = updatedIds.filter(id => id !== taskId);
    } else {
      updatedIds.push(taskId);
    }

    setCompletedTaskIds(updatedIds);

    // Update parent record state for seamless UI reactivity
    if (onUpdateRecord) {
      onUpdateRecord({
        ...record,
        swedishCompletedIds: updatedIds
      });
    }

    // Persist directly to Cloudflare D1
    try {
      const currentDate = record?.date || new Date().toISOString().split('T')[0];
      await apiPost('/api/swedish/progress', {
        date: currentDate,
        completed_task_ids: updatedIds
      });
    } catch (err) {
      console.error('Failed to save Swedish task completion to D1:', err);
    }
  };

  const getLocalizedTask = (task: SwedishTask) => {
    if (task.id === 'swe_verbs') {
      return {
        title: t('sweTaskVerbsTitle', 'افعال کاربردی سوئدی (Swedish Verbs)'),
        topic: t('sweTaskVerbsTopic', 'تسلط بر افعال حرکتی، زمان حال، گذشته و صرف افعال اصلی'),
        duration: t('sweTaskVerbsDur', '۱۰-۱۵ دقیقه')
      };
    }
    if (task.id === 'swe_employee') {
      return {
        title: t('sweTaskEmpTitle', 'واژگان حوزه استخدام و محیط کار (Employee / Arbete)'),
        topic: t('sweTaskEmpTopic', 'اصطلاحات اداری، درخواست کار، صحبت با همکاران و کارفرما'),
        duration: t('sweTaskEmpDur', '۱۰-۱۵ دقیقه')
      };
    }
    if (task.id === 'swe_independent') {
      return {
        title: t('sweTaskIndepTitle', 'مطالعه و ممارست مستقل روزانه'),
        topic: t('sweTaskIndepTopic', 'ثبت و پایش یادگیری مستقل انجام‌شده در سایر اپلیکیشن‌ها'),
        duration: t('sweTaskIndepDur', '۱۰-۱۵ دقیقه')
      };
    }
    return task;
  };

  const totalCount = DEFAULT_SWEDISH_TASKS.length;
  const completedCount = completedTaskIds.length;
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  
  const totalStudyMinutes = completedCount * 12;

  return (
    <div id="swedish-module-view" className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-yellow-500 animate-pulse" />
            {t('swedishModuleTitle', 'ماژول ۴ — پایش یادگیری زبان سوئدی')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('swedishModuleDesc', 'برای ثبت پیشرفت و اهدافی که در اپلیکیشن‌های دیگر یاد می‌گیرید؛ همراه با توصیه‌های روزانه ارتقای مهارت‌های SFI.')}
          </p>
        </div>
      </div>

      {/* Progress Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Study Progress */}
        <div className="premium-card p-5 dark:bg-gray-800 flex items-center gap-4 border-yellow-100 dark:border-yellow-950/40">
          <div className="w-12 h-12 rounded-2xl bg-yellow-500/15 flex items-center justify-center text-yellow-600 dark:text-yellow-400">
            <GraduationCap className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
              {t('swedishTodayProgress', 'پیشرفت اهداف امروز')}
            </span>
            <div className="flex items-baseline gap-1 justify-between">
              <span className="text-lg font-bold text-gray-800 dark:text-white font-display">
                {t('swedishTopicsCount', '{{completed}} از {{total}} سرفصل سوئدی', {
                  completed: formatNumberByLang(completedCount, lang),
                  total: formatNumberByLang(totalCount, lang)
                })}
              </span>
              <span className="text-xs font-mono text-yellow-600 dark:text-yellow-400 font-semibold">
                {formatNumberByLang(percent, lang)}%
              </span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-gray-700 h-1.5 rounded-full mt-2 overflow-hidden">
              <div 
                className="bg-yellow-500 h-full rounded-full transition-all duration-300" 
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Study Minutes Accumulator */}
        <div className="premium-card p-5 dark:bg-gray-800 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
              {t('swedishTodayStudyTime', 'زمان ثبت‌شده پایش امروز')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                {formatNumberByLang(totalStudyMinutes, lang)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('swedishMinutesEquivalent', 'دقیقه معادل')}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left: Simplified Checklists */}
        <div className="lg:col-span-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {t('swedishChecklistTitle', 'سرفصل‌های آماده برای ثبت خوداظهاری سوئدی')}
            </h3>
          </div>

          <div className="space-y-3">
            {DEFAULT_SWEDISH_TASKS.map(task => {
              const isCompleted = completedTaskIds.includes(task.id);
              const locTask = getLocalizedTask(task);
              
              return (
                <div 
                  key={task.id}
                  className={`premium-card p-4 dark:bg-gray-800 flex items-center justify-between border transition-all ${
                    isCompleted 
                      ? 'border-yellow-200 bg-yellow-50/10 dark:border-yellow-950/20' 
                      : 'hover:border-gray-200 dark:hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <button 
                      onClick={() => toggleTaskCompletion(task.id)}
                      className={`mt-0.5 p-1 rounded-xl border transition-all ${
                        isCompleted 
                          ? 'bg-yellow-500 border-yellow-500 text-white shadow-sm' 
                          : 'border-gray-300 dark:border-gray-600 hover:border-yellow-500'
                      }`}
                    >
                      <CheckCircle className={`w-5 h-5 ${isCompleted ? 'fill-current' : 'text-transparent'}`} />
                    </button>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm font-display">
                        {locTask.title}
                      </h4>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                        {t('swedishTopicDetail', 'جزییات سرفصل:')} {locTask.topic}
                      </p>
                    </div>
                  </div>

                  <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded-full font-medium shrink-0 font-mono">
                    {locTask.duration}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800 text-xs text-gray-500 leading-relaxed">
            {t('swedishNoticeBox', '💡 توجه داشته باشید: شما روند یادگیری این سرفصل‌ها را در برنامه‌های دیگر (مثل دولینگو، ممرایز یا کلاس‌های SFI) سپری کرده‌اید و در وکسا تنها برای پایش مستمر اهداف روزانه خود ثبت و تیک می‌زنید.')}
          </div>
        </div>

        {/* Right: Dynamic Tips Section */}
        <div className="lg:col-span-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-yellow-500 animate-pulse" />
              {t('swedishDailyTipsTitle', 'توصیه‌های کاربردی روزانه (۳ نکته کلیدی سوئدی)')}
            </h3>
            
            <button
              onClick={generateNewAiTips}
              disabled={isLoadingTips || isRotating}
              className="text-xs text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 font-bold flex items-center gap-1 bg-yellow-500/10 dark:bg-yellow-500/5 px-2.5 py-1.5 rounded-lg border border-yellow-500/20 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
            >
              {isLoadingTips ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className={`w-3.5 h-3.5 ${isRotating ? 'animate-spin' : ''}`} />
              )}
              {t('swedishGetNewTips', 'دریافت ۳ نکته جدید با AI')}
            </button>
          </div>

          <div className="space-y-3.5">
            {tips.length === 0 && isLoadingTips ? (
              <div className="p-8 text-center text-xs text-gray-400 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                <span>در حال اتصال به هوش مصنوعی و تولید توصیه‌های هوشمند زبان سوئدی...</span>
              </div>
            ) : (
              tips.map((tip, idx) => (
                <div 
                  key={idx} 
                  className="premium-card p-4 dark:bg-gray-800 border-l-4 border-l-yellow-500 bg-gradient-to-r from-yellow-500/5 to-transparent dark:from-yellow-950/10 flex items-start gap-3.5 animate-fadeIn"
                  style={{ animationDelay: `${idx * 150}ms` }}
                >
                  <div className="w-6 h-6 rounded-lg bg-yellow-500/10 flex items-center justify-center text-yellow-600 dark:text-yellow-400 font-bold text-xs font-mono shrink-0">
                    {formatNumberByLang(idx + 1, lang)}
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                    {tip}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="premium-card p-4 dark:bg-gray-800 bg-gradient-to-tr from-indigo-500/10 to-transparent flex items-center justify-between border border-indigo-100 dark:border-indigo-950/40">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              <Key className="w-4 h-4 text-indigo-500" />
              <span>{t('swedishDailyKeyword', 'کلید واژه امروز:')} <strong className="text-indigo-600 dark:text-indigo-400 font-mono">{keyword}</strong></span>
            </div>
            <span className="text-[10px] text-gray-400">{keywordPronounce}</span>
          </div>
        </div>

      </div>

    </div>
  );
}
